import merge from "lodash/merge"
import { DeepPartial, ISeriesApi, LineType, LogicalRange } from "lightweight-charts";
import Chart from "../chart";
import { ChartCandle, LineData, LineSeriesOptions } from "../../chartV2/chart.typings";
import { lineSeriesOptions } from "../../../assets/constants";

class LineSeries {
    data: LineData[] = [];
    rawData: ChartCandle[] = [];
    options: DeepPartial<LineSeriesOptions>; // raw options as passed to class
    chart: Chart;
    seriesApi: ISeriesApi<any>;

    constructor(chart: Chart, options: DeepPartial<LineSeriesOptions>) {
        this.options = options
        this.chart = chart;
        this.init();
        this.seriesApi = this.addSeriesToChart()
        this.seriesApi.applyOptions(this.options)
    }

    init() { }


    addSeriesToChart() {
        return this.chart.chartApi.addLineSeries()
    }

    getDefaultOptions(): DeepPartial<LineSeriesOptions> {
        const options: LineSeriesOptions = {
            lineType: this.options.lineType || LineType.Simple,
            source: "close",
            lineWidth: this.options.lineWidth || 1,
            visible: this.options.visible ?? true
        };
        return merge({...lineSeriesOptions}, options, this.options)
    }
    onOptionsUpdate(options: DeepPartial<LineSeriesOptions>): void {
        if ('source' in options) {
            this._setData(this.rawData)
        }
    }

    generateSeriesData(data: ChartCandle[]): LineData[] {
        const result: LineData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);

        const source = this.options.source??'close'

        data.forEach((candle, i) => {
            if (candle[source] === undefined) return;
            const value = candle[source] as number
            // Change mode plots OI delta from `series`; skip zero so flat/no-change bars are not drawn.
            if (source === 'series' && value === 0) return
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0

            const point: LineData = {
                ...candle,
                value
            }

            // lightweight-charts requires strictly ascending, unique timestamps.
            // Back/forward candle stitching can occasionally produce consecutive
            // points sharing a time (or out of order); collapse them here so
            // seriesApi.setData does not assert.
            const last = result[result.length - 1]
            if (last) {
                const lastTime = last.time as number
                const pointTime = point.time as number
                if (pointTime === lastTime) {
                    result[result.length - 1] = point
                    return
                }
                if (pointTime < lastTime) {
                    return
                }
            }
            result.push(point)
        })

        return result;
    }

    _setData(data: ChartCandle[]) {
        this.setData(data)
        this.seriesApi.setData(this.data)
    }
    setData(data: ChartCandle[]): void {
        this.rawData = data;
        this.data = this.generateSeriesData(data)
    }
    updateData(): void {
        const result = this.generateSeriesData(this.rawData.slice(-10))
        const lastCandle = this.data[this.data.length - 1];
        const updatedCandle = result[result.length - 1];
        if (lastCandle && lastCandle.time === updatedCandle.time) {
            this.data[this.data.length - 1] = updatedCandle
            return;
        }
        this.data.push(updatedCandle)
    }

    _update(candle: ChartCandle) {
        const prevPoint = this.data[this.data.length - 1]
        const lastCandle = this.rawData[this.rawData.length - 1];
        if (lastCandle && lastCandle.time === candle.time) {
            this.rawData[this.rawData.length - 1] = candle
        } else {
            this.rawData.push(candle);
        }
        const source = this.options.source ?? 'close'
        if (source === 'series' && (candle.series === 0 || candle.series === undefined)) {
            return
        }
        this.updateData()
        const nextPoint = this.data[this.data.length - 1]
        if (prevPoint && nextPoint && prevPoint.time === nextPoint.time && prevPoint.value === nextPoint.value) {
            return
        }
        this.seriesApi.update(nextPoint)
    }

    _setDataAndUpdateCandles(data: ChartCandle[][]) {
        const [totalData, newData] = data
        this.rawData = totalData
        this.data = this.generateSeriesData(totalData)
        if(newData.length>0) {
            const result = this.generateSeriesData(newData)
            const lastCandleTime = this.data?.slice?.(-1)?.[0]?.time
            const source = this.options.source ?? 'close'
            result.forEach(candle=>{
                if(candle.time >= lastCandleTime) {
                    if (source === 'series' && candle.value === 0) return
                    this.seriesApi.update(candle)
                }
            })
        }
    }

    getBarsInfo(range: LogicalRange | null) {
        if (!range) return
        return this.seriesApi.barsInLogicalRange(range)
    }

    updateOptions(options: DeepPartial<LineSeriesOptions>) {
        this.options = { ...this.options, ...options };
        this.seriesApi.applyOptions(this.options)
        this.onOptionsUpdate(options)
    }

    updateConfig() {

    }


    getData(): LineData[] {
        return this.data
    }

    destroy() {
        this.data = [];
        this.rawData = [];
        this.options = {}
        this.chart.chartApi.removeSeries(this.seriesApi)
    };

}

export default LineSeries;